import React, { useState } from 'react';
import { WithStyles } from '@material-ui/styles';
import {
  Box, Grid, Typography, IconButton, Paper, Dialog, TextField,
} from '@material-ui/core';
import RefreshRounded from '@material-ui/icons/RefreshRounded';
import EditIcon from '@material-ui/icons/Edit';
import CloseIcon from '@material-ui/icons/Close';
import Spinner from '@material-ui/core/CircularProgress';
import styles from './styles';
import CustomButton from '../CustomButton';
import { beautifyErrors, ValidationErrors } from '../../utils/helpers';
import { SmartLock, SmartLockCode } from '../../redux/types/smartLocks';
import { smartLockCodeSchema } from './validation';
import { ActionPayload } from '../../types/main';
import {
  deleteSmartLockCodeRequest,
  refreshSmartLockDetailsRequest,
  smartLockDetailsRequest,
  updateSmartLockCodeRequest,
} from '../../redux/actions/smartLocks';
import { usePermissions } from '../../hooks/usePermissions';

// Props from Redux (via index.ts)
type ReduxProps = {
  loading: boolean;
  refreshing: boolean;
  smartLockDetails: SmartLock | null;
  loadSmartLockDetails: (
    smartLockSerial: ActionPayload<typeof smartLockDetailsRequest>
  ) => void;
  refreshSmartLockDetails: (
    smartLockSerial: ActionPayload<typeof refreshSmartLockDetailsRequest>
  ) => void;
  updateSmartLockCode: (
    smartLockCode: ActionPayload<typeof updateSmartLockCodeRequest>
  ) => void;
  deleteSmartLockCode: (
    smartLockCode: ActionPayload<typeof deleteSmartLockCodeRequest>
  ) => void;
};

// Props from parent component (SmartLockPanel) - only what's actually used
type ParentProps = {
  smartLockSerial: string;
  accessCodes: SmartLockCode[];
  newCode: SmartLockCode | null;
  setNewCode: (code: SmartLockCode | null) => void;
  setSeconds: (seconds: number) => void;
  validationErrors: ValidationErrors<SmartLockCode>;
  setValidationErrors: (errors: ValidationErrors<SmartLockCode>) => void;
};

type Props = ReduxProps & ParentProps & WithStyles<typeof styles>;

const SmartLockCodesForm: React.FC<Props> = ({
  classes,
  loading,
  refreshing,
  smartLockDetails,
  smartLockSerial,
  updateSmartLockCode,
  deleteSmartLockCode,
  // Props from parent
  accessCodes,
  newCode,
  setNewCode,
  setSeconds,
  validationErrors,
  setValidationErrors,
  refreshSmartLockDetails,
  }) => {
  const [openModal, setOpenModal] = useState<boolean>(false);

  const { canEdit } = usePermissions();

  const handleNewCode = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (!newCode || (name === "code" && Number.isNaN(Number(value)))) {
      return;
    }

    setNewCode({ ...newCode, [name]: value });
  };

  const saveNewCode = () => {
    try {
      if (!newCode) {
        return;
      }

      const isSupportedCodeLengths = smartLockDetails?.supported_code_lengths?.includes(
        newCode.code.length
      );

      if (!isSupportedCodeLengths) {
        setValidationErrors({ code: "invalid code length" });
        return;
      }

      smartLockCodeSchema.validateSync(newCode, {
        abortEarly: false
      });

      setValidationErrors({});

      const existedCode = accessCodes.find(code => code.code === newCode.code);

      if (existedCode) {
        setValidationErrors({
          code: `code ${newCode.code} already exists as ${existedCode.name}`
        });
        return;
      }

      updateSmartLockCode({
        device_id: smartLockSerial,
        code: newCode.code,
        name: newCode.name,
        access_code_id: newCode.access_code_id
      });

      setNewCode(null);
      setSeconds(0);
      setOpenModal(false);
    } catch (errors) {
      setValidationErrors(beautifyErrors(errors));
    }
  };

  const deleteCode = (accessCodeId: string) => {
    deleteSmartLockCode({ smartLockSerial, access_code_id: accessCodeId });
  };

  const addNewCodeButton = () => (
    <CustomButton
      variant="orange"
      disabled={loading || !canEdit}
      onClick={() => {
        setOpenModal(true);
        setNewCode({ code: '', name: '' });
      }}
      style={{
        width: '30%',
      }}
    >
      Add New Code
    </CustomButton>
  );

  return (
    <>
      {accessCodes.length > 0 && (
        <Grid item xs={12}>
          <Paper style={{ padding: 32, height: '100%', position: 'relative' }}>
            <Box display="flex" alignItems="center" mb={4} justifyContent="space-between">
              <Typography variant="h6" className={classes.title}>Smart Lock Codes</Typography>
              <CustomButton
                variant="orange"
                onClick={() => refreshSmartLockDetails(smartLockSerial)}
                endIcon={<RefreshRounded />}
                disabled={loading || refreshing || !canEdit}
              >
                Refresh
              </CustomButton>
            </Box>

            {accessCodes.map((item) => (
              <Grid
                container
                alignItems="center"
                spacing={2}
                className={classes.codeCard}
                key={item.access_code_id}
              >
                <Grid item xs={3}>
                  <Box display="flex" alignItems="center">
                    <Box
                      width={8}
                      height={8}
                      borderRadius="50%"
                      bgcolor={item.status === 'set' ? '#04A777' : '#1E72C5'}
                    />
                    <Box
                      fontSize="16px"
                      color={item.status === 'set' ? '#04A777' : '#1E72C5'}
                      ml={1}
                    >
                      {item.status === 'set' ? 'Active' : 'Pending'}
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={3}>
                  <Typography variant="caption" className={classes.codeLabel}>
                    CODE
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    className={classes.codeValue}
                    noWrap
                  >
                    {item.code}
                  </Typography>
                </Grid>

                <Grid item xs={4}>
                  <Typography variant="caption" className={classes.codeLabel}>
                    NAME
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    className={classes.codeValue}
                    noWrap
                  >
                    {item.name}
                  </Typography>
                </Grid>

                <Grid item xs={2}>
                  <Box display="flex" justifyContent="flex-end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setNewCode({
                          code: item.code,
                          name: item.name,
                          access_code_id: item.access_code_id,
                        });
                        setOpenModal(true);
                      }}
                      disabled={!canEdit}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        if (!item?.access_code_id) {
                          return;
                        }

                        deleteCode(item?.access_code_id);
                      }}
                      disabled={!canEdit}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Box>
                </Grid>
              </Grid>
            ))}
            {accessCodes.length < 8 && (
              <Box mt={3} justifyContent="center" display="flex" width="100%">
                {addNewCodeButton()}
              </Box>
            )}
            {loading && (
              <Box>
                <Spinner className={classes.progress} />
              </Box>
            )}
          </Paper>
        </Grid>
      )}

      {accessCodes.length === 0 && (
        <Grid item xs={12}>
          <Paper style={{ padding: 32, height: '100%' }}>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              height="100%"
              textAlign="center"
            >
              <Typography variant="h6" gutterBottom>
                Smart lock has no codes
              </Typography>

              <Box mt={3} justifyContent="center" display="flex" width="100%">
                {addNewCodeButton()}
              </Box>
            </Box>
          </Paper>
        </Grid>
      )}

      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        PaperProps={{
          className: classes.modalWrapper,
        }}
      >
        <Typography variant="h6" gutterBottom className={classes.modalTitle}>New Smart Lock Code</Typography>

        <Grid container spacing={2} className={classes.fieldWrapper}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              placeholder="Code"
              variant="outlined"
              size="small"
              margin="none"
              name="code"
              className={classes.textField}
              inputProps={{ maxLength: 8 }}
              value={newCode?.code}
              onChange={handleNewCode}
              error={Boolean(validationErrors.code)}
              helperText={validationErrors.code}
              disabled={!canEdit}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              placeholder="Name"
              variant="outlined"
              size="small"
              margin="none"
              name="name"
              className={classes.textField}
              inputProps={{ maxLength: 16 }}
              value={newCode?.name}
              onChange={handleNewCode}
              error={Boolean(validationErrors.name)}
              helperText={validationErrors.name}
              disabled={!canEdit}
            />
          </Grid>
        </Grid>

        <Box className={classes.buttonWrapper}>
          <CustomButton
            variant="orange"
            fullWidth
            onClick={saveNewCode}
            disabled={!canEdit}
          >
            Save
          </CustomButton>

          <CustomButton
            variant="white"
            fullWidth
            onClick={() => {
              setOpenModal(false);
              setNewCode(null);
            }}
            disabled={!canEdit}
          >
            Cancel
          </CustomButton>
        </Box>
      </Dialog>

    </>
  );
};

export default SmartLockCodesForm;
